import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { cloneDeep, isNil, size } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { useTheme } from '@mui/material/styles';
import { Box, Button, Typography, Collapse, TextField, IconButton } from '@mui/material';

import { ReactComponent as CloseIcon } from 'assets/images/icons/icn_close.svg';
import { ReactComponent as ChevronSolidIcon } from 'assets/images/icons/icn_chevron_solid.svg';
import { ReactComponent as AddIcon } from 'assets/images/icons/btn_add.svg';
import { ReactComponent as AddIconDisabled } from 'assets/images/icons/btn_add_disabled.svg';
import { ReactComponent as DeleteIcon } from 'assets/images/icons/icn_table_cell_Bin.svg';

// Utils
import { validDomainName, validUrl, validateIPv4 } from 'utils/validate';

// Hooks
import useDataSourceManager from 'hooks/useDataSourceManager';
import useAuthScope from 'hooks/useAuthScope';

// Components
import LinkModal from 'components/LinkModal';
import GlobalAlert from 'components/Notification';
import ModalContainer from 'components/ModalContainer';
import { MenuSelect } from 'components/Select/menuSelect';
import { RadioButton } from 'components/buttons/RadioButton';
import { SolidButton } from 'components/buttons/SolidButton';
import { OutlineButton } from 'components/buttons/OutlineButton';
import { OutlineRectangleButton } from 'components/buttons/OutlineRectangleButton';

import Loading from '../components/Loading';
import UrlInput from '../components/UrlInput';
import SelectMenu from '../components/SelectMenu';
import TooltipNotice from '../components/TooltipNotice';
import { TypeSelectMenu } from '../components/TypeSelectMenu';

import mainStyle from '../mainStyle.module.scss';

// Const
import { captivePortalConfig, authTypeConfig, applyAuthTypeFieldDefaults } from '../const';

const checkMockup = process.env.REACT_APP_IS_MOCKUP && process.env.REACT_APP_IS_MOCKUP === 'true' ? true : false;

const MAX_WALLED_GARDEN_ENTRIES = 20;

const isValidWalledGardenValue = value => {
  const normalizedValue = value?.trim();
  if (!normalizedValue) return false;

  if (validateIPv4(normalizedValue) || validDomainName(normalizedValue)) {
    return true;
  }

  const [ipAddress, prefixLength] = normalizedValue.split('/');
  if (!ipAddress || prefixLength === undefined || !validateIPv4(ipAddress) || !/^\d+$/.test(prefixLength)) {
    return false;
  }

  const prefix = Number(prefixLength);
  return prefix >= 0 && prefix <= 32;
};

const AddCaptivePortalModal = ({ openCreateModal, setOpenCreateModal, tableData, formData, setFormData, reloadPage, initCaptivePortal, isUnityPro }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { auth } = useAuthScope();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const [createCaptivePortalUuid, _setCreateCaptivePortalUuid] = useState(uuidv4());

  const [hasDuplicateName, setHasDuplicateName] = useState(false);
  const [openGeneral, setOpenGeneral] = useState(true);
  const [openAdvancedSettings, setOpenAdvancedSettings] = useState(true);
  const [walledGardenInputs, setWalledGardenInputs] = useState([]);

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: '',
    message: '',
  });

  const macLimitExceeded = formData?.whitelistList?.find(item => item.isActive === true)?.macListLength > 64;

  const textfieldStyle = {
    width: '300px',
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.shape.customBorderRadius[1],
      height: '30px',
      '& fieldset': {
        borderColor: theme.palette.primary.borderGreyPrimary,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.mainHover,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    // Hide native number spinners (WebKit)
    '& .MuiOutlinedInput-root input[type="number"]::-webkit-outer-spin-button, & .MuiOutlinedInput-root input[type="number"]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    // Firefox / generic appearance
    '& .MuiOutlinedInput-root input[type="number"]': {
      MozAppearance: 'textfield',
      appearance: 'textfield',
    },
  };

  const rowStyle = {
    paddingLeft: '32px',
    marginTop: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  };

  const labelStyle = {
    width: '250px',
    color: theme.palette.primary.greyPrimary,
    fontSize: theme.typography.customFontSize[2],
    fontWeight: theme.typography.customFontWeight[0],
  };

  const checkSaveProRule = () => {
    const basicTypeArray = ['CLICK_THROUGH', 'USERNAME_PASSWORD'];
    if (!isUnityPro && !basicTypeArray.includes(formData?.authTypeList?.find(item => item.isActive)?.value)) {
      setIsLinkModalOpen(true);
      return; // Open the link modal first.
    }

    handleClickCreateBtn();
  };

  const handleChangeWalledGardenItem = (index, newValue) => {
    setFormData(prev => ({
      ...prev,
      walledGardenList: prev.walledGardenList.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              label: newValue,
              value: newValue?.trim(),
            }
          : item,
      ),
    }));
  };

  const handleAddWalledGardenItem = () => {
    if ((formData?.walledGardenList?.length || 0) >= MAX_WALLED_GARDEN_ENTRIES) return;

    setFormData(prev => ({
      ...prev,
      walledGardenList: [...(prev.walledGardenList || []), { label: '', value: '' }],
    }));

    // keep the visible input values in sync when adding
    setWalledGardenInputs(prev => [...(prev || []), '']);
  };

  const handleRemoveWalledGardenItem = index => {
    setFormData(prev => {
      const nextList = prev.walledGardenList.filter((_, itemIndex) => itemIndex !== index);
      return {
        ...prev,
        walledGardenList: nextList.length > 0 ? nextList : [{ label: '', value: '' }],
      };
    });

    // also remove the corresponding input value so new inputs don't reuse deleted values
    setWalledGardenInputs(prev => {
      const next = (prev || []).filter((_, i) => i !== index);
      return next.length > 0 ? next : [''];
    });
  };

  const handleChangeItem = (list, newValue) => {
    const newFormData = cloneDeep(formData);

    if (list === 'authTypeList') {
      const currentType = formData?.authTypeList?.find(item => item.isActive)?.value;
      if (currentType === newValue) return;

      if (newFormData?.[list]?.length > 0) {
        newFormData[list] = newFormData[list].map(item => ({
          ...item,
          isActive: item.value === newValue,
        }));
      }

      applyAuthTypeFieldDefaults(newFormData, newValue);
      setFormData(newFormData);
      return;
    }

    if (newFormData?.[list]?.length > 0) {
      newFormData[list] = newFormData?.[list]?.map(item => ({
        ...item,
        isActive: item.value === newValue,
      }));
    }
    setFormData(newFormData);
  };

  const handleCloseModal = () => {
    setOpenCreateModal(false);
    setHasDuplicateName(false);
    setOpenGeneral(true);
    setOpenAdvancedSettings(true);
    setWalledGardenInputs([]);
  };

  const handleClickCreateBtn = () => {
    setIsLoading(true);

    const standalone = !auth.orgs || Object.keys(auth.orgs).length === 0;
    const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

    const apiConfig = {
      org: `${baseUrl}/settings/captivePortals.create`,
      site: `${baseUrl}/sites/:siteId/settings/captivePortals.create`,
    };

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };

    const apiKey = apiConfig[auth.scopeLevel];
    const mode = standalone ? 'standalone' : 'nonStandalone';
    const restfulParams = restfulParamsConfig[mode];

    const isSelectedAuthType = formData?.authTypeList?.find(item => item.isActive)?.value || 'CLICK_THROUGH';

    const payload = {
      name: formData?.profileName || '',
      authType: isSelectedAuthType,
      simultaneousLogin: formData?.simultaneousLoginOption === 'ENABLE' ? true : false,
      macWhiteList: formData?.whitelistList?.find(item => item.isActive)?.value || '',
      walledGarden: formData?.preAuthAccessOption === 'ENABLE' ? true : false,
      walledGardenList: formData?.preAuthAccessOption === 'ENABLE' ? getWalledGardenListValue() : [],
    };

    switch (isSelectedAuthType) {
      case 'USERNAME_PASSWORD':
        payload['idleTimeoutOption'] = getIdleTimeoutOption();
        payload['idleTimeout'] = getIdleTimeoutValue();
        payload['authTimeoutOption'] = getAuthTimeoutOption();
        payload['authTimeout'] = getAuthTimeoutValue();
        payload['authPerDayOption'] = getAuthPerDayOption();
        payload['authPerDay'] = getAuthPerDayValue();
        payload['reAuthIntervalOption'] = getReAuthIntervalOption();
        payload['reAuthInterval'] = getReAuthIntervalValue();
        payload['userProfile'] = getUserProfileValue();
        payload['landingPageOption'] = getLandingPageOption();
        payload['landingPageUrl'] = getLandingPageValue();
        break;
      case 'VOUCHER':
        payload['idleTimeoutOption'] = getIdleTimeoutOption();
        payload['idleTimeout'] = getIdleTimeoutValue();
        payload['landingPageOption'] = getLandingPageOption();
        payload['landingPageUrl'] = getLandingPageValue();
        break;
      case 'REMOTE_RADIUS':
        payload['idleTimeoutOption'] = getIdleTimeoutOption();
        payload['idleTimeout'] = getIdleTimeoutValue();
        payload['authTimeoutOption'] = getAuthTimeoutOption();
        payload['authTimeout'] = getAuthTimeoutValue();
        payload['authPerDayOption'] = getAuthPerDayOption();
        payload['authPerDay'] = getAuthPerDayValue();
        payload['reAuthIntervalOption'] = getReAuthIntervalOption();
        payload['reAuthInterval'] = getReAuthIntervalValue();
        payload['radiusProfile'] = getRadiusProfileValue();
        payload['landingPageOption'] = getLandingPageOption();
        payload['landingPageUrl'] = getLandingPageValue();
        break;
      case 'LDAP':
        payload['idleTimeoutOption'] = getIdleTimeoutOption();
        payload['idleTimeout'] = getIdleTimeoutValue();
        payload['authTimeoutOption'] = getAuthTimeoutOption();
        payload['authTimeout'] = getAuthTimeoutValue();
        payload['authPerDayOption'] = getAuthPerDayOption();
        payload['authPerDay'] = getAuthPerDayValue();
        payload['reAuthIntervalOption'] = getReAuthIntervalOption();
        payload['reAuthInterval'] = getReAuthIntervalValue();
        payload['ldapProfile'] = getLdapProfileValue();
        payload['landingPageOption'] = getLandingPageOption();
        payload['landingPageUrl'] = getLandingPageValue();
        break;
      case 'MAC_ADDRESS':
        payload['idleTimeoutOption'] = getIdleTimeoutOption();
        payload['idleTimeout'] = getIdleTimeoutValue();
        payload['radiusProfile'] = getRadiusProfileValue();
        break;
      case 'EXTERNAL_CAPTIVE_PORTAL':
        payload['idleTimeoutOption'] = getIdleTimeoutOption();
        payload['idleTimeout'] = getIdleTimeoutValue();
        payload['externalCaptivePortal'] = getExternalCaptivePortalValue();
        payload['landingPageOption'] = getLandingPageOption();
        payload['landingPageUrl'] = getLandingPageValue();
        break;
      default:
        // CLICK_THROUGH
        payload['idleTimeoutOption'] = getIdleTimeoutOption();
        payload['idleTimeout'] = getIdleTimeoutValue();
        payload['authTimeoutOption'] = getAuthTimeoutOption();
        payload['authTimeout'] = getAuthTimeoutValue();
        payload['authPerDayOption'] = getAuthPerDayOption();
        payload['authPerDay'] = getAuthPerDayValue();
        payload['reAuthIntervalOption'] = getReAuthIntervalOption();
        payload['reAuthInterval'] = getReAuthIntervalValue();
        payload['landingPageOption'] = getLandingPageOption();
        payload['landingPageUrl'] = getLandingPageValue();
        break;
    }

    sendRequest(apiKey, createCaptivePortalUuid, restfulParams, payload);
  };

  const getIdleTimeoutOption = () => {
    return formData?.idleTimeoutOption || 'DEFAULT';
  };

  const getIdleTimeoutValue = () => {
    if (formData?.idleTimeoutOption === 'DEFAULT') {
      return formData?.idleTimeoutList?.find(item => item.isActive)?.value;
    } else {
      return formData?.idleTimeoutLabel; // CUSTOM
    }
  };

  const getAuthTimeoutOption = () => {
    return formData?.authTimeoutOption || 'DISABLE';
  };

  const getAuthTimeoutValue = () => {
    if (formData?.authTimeoutOption === 'ENABLE') {
      return formData?.authTimeoutList?.find(item => item.isActive)?.value;
    } else if (formData?.authTimeoutOption === 'CUSTOM') {
      return formData?.authTimeoutLabel;
    } else {
      return 2; // DISABLE
    }
  };

  const getAuthPerDayOption = () => {
    return formData?.authPerDayOption || 'CUSTOM';
  };

  const getAuthPerDayValue = () => {
    if (formData?.authPerDayOption === 'CUSTOM') {
      return formData?.authPerDayLabel;
    } else {
      return 1; // UNLIMIT
    }
  };

  const getReAuthIntervalOption = () => {
    return formData?.reAuthIntervalOption || 'DISABLE';
  };

  const getReAuthIntervalValue = () => {
    if (formData?.reAuthIntervalOption === 'ENABLE') {
      return formData?.reAuthIntervalList?.find(item => item.isActive)?.value;
    } else if (formData?.reAuthIntervalOption === 'CUSTOM') {
      return formData?.reAuthIntervalLabel;
    } else {
      return 1; // DISABLE
    }
  };

  const getUserProfileValue = () => {
    return formData?.userProfileList?.find(item => item.isActive)?.value || '';
  };

  const getRadiusProfileValue = () => {
    return formData?.radiusProfileList?.find(item => item.isActive)?.value || '';
  };

  const getLdapProfileValue = () => {
    return formData?.ldapProfileList?.find(item => item.isActive)?.value || '';
  };

  const getExternalCaptivePortalValue = () => {
    if (formData?.externalCaptivePortalProtocol && formData?.externalCaptivePortalLabel) {
      return `${formData?.externalCaptivePortalProtocol}://${formData?.externalCaptivePortalLabel}`;
    } else {
      return '';
    }
  };

  const getLandingPageOption = () => {
    return formData?.landingPageOption || 'NO_SPECIFIC';
  };

  const getLandingPageValue = () => {
    if (formData?.landingPageOption === 'CUSTOM') {
      return `${formData?.landingPageUrlProtocol}://${formData?.landingPageUrlLabel}`;
    } else {
      return '';
    }
  };

  const getWalledGardenListValue = () => {
    return formData?.walledGardenList?.map(item => item?.label?.trim()).filter(Boolean) || [];
  };

  const checkProfileNameInvalid = () => {
    return hasDuplicateName || !formData?.profileName;
  };

  const checkIdleTimeoutInvalid = () => {
    if (formData?.idleTimeoutOption === 'CUSTOM') {
      return formData?.idleTimeoutLabel < 2 || formData?.idleTimeoutLabel > 1440;
    }
    return false;
  };

  const checkAuthTimeoutInvalid = () => {
    if (formData?.authTimeoutOption === 'CUSTOM') {
      return formData?.authTimeoutLabel < 2 || formData?.authTimeoutLabel > 1440;
    } else if (formData?.authTimeoutOption === 'DISABLE') {
      return false;
    }
    return false;
  };

  const checkAuthPerDayInvalid = () => {
    if (formData?.authPerDayOption === 'CUSTOM') {
      return formData?.authPerDayLabel < 1 || formData?.authPerDayLabel > 720;
    }
    return false;
  };

  const checkReAuthIntervalInvalid = () => {
    if (formData?.reAuthIntervalOption === 'CUSTOM') {
      return formData?.reAuthIntervalLabel < 1 || formData?.reAuthIntervalLabel > 1440;
    }
    return false;
  };

  const checkUserProfileInvalid = () => {
    return !formData?.userProfileList?.find(item => item.isActive)?.value;
  };

  const checkRadiusProfileInvalid = () => {
    return !formData?.radiusProfileList?.find(item => item.isActive)?.value;
  };

  const checkLdapProfileInvalid = () => {
    return !formData?.ldapProfileList?.find(item => item.isActive)?.value;
  };

  const checkExternalCaptivePortalInvalid = () => {
    if (formData?.externalCaptivePortalProtocol || formData?.externalCaptivePortalLabel) {
      return !formData?.externalCaptivePortalLabel || !validUrl(formData?.externalCaptivePortalLabel);
    }
    return false;
  };

  const checkLandingPageInvalid = () => {
    if (formData?.landingPageOption === 'CUSTOM') {
      return !formData?.landingPageUrlLabel || !validUrl(formData?.landingPageUrlLabel);
    }
    return false;
  };

  const checkWalledGardenEntryInvalid = () => {
    if (formData?.preAuthAccessOption === 'ENABLE') {
      const hasInvalidWalledGardenEntry =
        formData?.walledGardenList?.some(item => {
          const currentValue = item?.label || '';
          return currentValue && !isValidWalledGardenValue(currentValue);
        }) || false;
      return hasInvalidWalledGardenEntry;
    }
    return false;
  };

  const checkWalledGardenDuplicate = () => {
    const walledGardenValues = formData?.walledGardenList?.map(item => item?.label?.trim()).filter(Boolean) || [];
    const uniqueValues = new Set(walledGardenValues);
    return uniqueValues.size !== walledGardenValues.length;
  };

  // Check whether the value at a specific walled garden input index is duplicated
  const isWalledGardenValueDuplicate = index => {
    const values = (walledGardenInputs || []).map(v => v?.trim()).filter(Boolean);
    const val = (walledGardenInputs?.[index] || '').trim();
    if (!val) return false;
    const count = values.filter(v => v === val).length;
    return count > 1;
  };

  const checkWalledGardenInvalid = () => {
    if (formData?.preAuthAccessOption === 'ENABLE') {
      const walledGardenEntry = checkWalledGardenEntryInvalid();
      const walledGardenDuplicate = checkWalledGardenDuplicate();
      const walledGardenCountInvalid = (formData?.walledGardenList?.length || 0) > MAX_WALLED_GARDEN_ENTRIES;
      const walledGardenEmptyInvalid = formData?.walledGardenList?.some(item => !item?.label?.trim());
      const walledGardenAllEmptyInvalid = formData?.walledGardenList?.every(item => !item?.label?.trim());
      return walledGardenEntry || walledGardenDuplicate || walledGardenCountInvalid || walledGardenEmptyInvalid || walledGardenAllEmptyInvalid;
    }
    return false;
  };

  const checkConfigInvalid = () => {
    let isInvalid = false;

    switch (formData?.authTypeList?.find(item => item.isActive)?.value) {
      case 'USERNAME_PASSWORD':
        isInvalid =
          checkIdleTimeoutInvalid() ||
          checkAuthTimeoutInvalid() ||
          checkAuthPerDayInvalid() ||
          checkReAuthIntervalInvalid() ||
          checkUserProfileInvalid() ||
          checkLandingPageInvalid() ||
          checkWalledGardenInvalid();
        break;
      case 'VOUCHER':
        isInvalid = checkLandingPageInvalid() || checkWalledGardenInvalid();
        break;
      case 'REMOTE_RADIUS':
        isInvalid =
          checkIdleTimeoutInvalid() ||
          checkAuthTimeoutInvalid() ||
          checkAuthPerDayInvalid() ||
          checkReAuthIntervalInvalid() ||
          checkRadiusProfileInvalid() ||
          checkLandingPageInvalid() ||
          checkWalledGardenInvalid();
        break;
      case 'LDAP':
        isInvalid =
          checkIdleTimeoutInvalid() ||
          checkAuthTimeoutInvalid() ||
          checkAuthPerDayInvalid() ||
          checkReAuthIntervalInvalid() ||
          checkLdapProfileInvalid() ||
          checkLandingPageInvalid() ||
          checkWalledGardenInvalid();
        break;
      case 'MAC_ADDRESS':
        isInvalid = checkIdleTimeoutInvalid() || checkRadiusProfileInvalid() || checkWalledGardenInvalid();
        break;
      case 'EXTERNAL_CAPTIVE_PORTAL':
        isInvalid = checkIdleTimeoutInvalid() || checkExternalCaptivePortalInvalid() || checkLandingPageInvalid() || checkWalledGardenInvalid();
        break;
      default:
        // CLICK_THROUGH
        isInvalid = checkAuthTimeoutInvalid() || checkAuthPerDayInvalid() || checkReAuthIntervalInvalid() || checkLandingPageInvalid() || checkWalledGardenInvalid();
        break;
    }

    return checkProfileNameInvalid() || macLimitExceeded || isInvalid;
  };

  // Check which authType is selected and get the corresponding settings from authTypeConfig
  const selectedAuthType = useMemo(() => {
    return formData?.authTypeList?.find(item => item.isActive)?.value ?? 'CLICK_THROUGH';
  }, [formData?.authTypeList]);

  const authTypeSetting = authTypeConfig?.[selectedAuthType] ?? {};

  const showSimultaneousLoginInfo = authTypeSetting.simultaneousLogin ?? false;
  const showIdleTimeoutInfo = authTypeSetting.idleTimeout ?? false;
  const showAuthTimeoutInfo = authTypeSetting.authTimeout ?? false;
  const showAuthPerDayInfo = authTypeSetting.authPerDay ?? false;
  const showReAuthIntervalInfo = authTypeSetting.reAuthInterval ?? false;
  const showUserProfileInfo = authTypeSetting.userProfile ?? false;
  const showRadiusProfileInfo = authTypeSetting.radiusProfile ?? false;
  const showLdapProfileInfo = authTypeSetting.ldapProfile ?? false;
  const showExternalCaptivePortalInfo = authTypeSetting.externalCaptivePortal ?? false;
  const showLandingPageInfo = authTypeSetting.landingPage ?? false;
  const showWhitelistInfo = authTypeSetting.whitelist ?? false;
  const showPreAuthAccessInfo = authTypeSetting.preAuthAccess ?? false;

  useEffect(() => {
    setWalledGardenInputs((formData?.walledGardenList || []).map(item => item?.label || ''));
  }, [formData?.walledGardenList]);

  useEffect(() => {
    if (openCreateModal) {
      setFormData(prev => ({
        ...cloneDeep(captivePortalConfig),
        userProfileList: prev.userProfileList.map((item, index) => ({
          ...item,
          isActive: index === 0,
        })),
        radiusProfileList: prev.radiusProfileList.map((item, index) => ({
          ...item,
          isActive: index === 0,
        })),
        ldapProfileList: prev.ldapProfileList.map((item, index) => ({
          ...item,
          isActive: index === 0,
        })),
        whitelistList: prev.whitelistList.map((item, index) => ({
          ...item,
          isActive: index === 0,
        })),
      }));
    }
  }, [openCreateModal]);

  useEffect(() => {
    const isCreateResponse = getData(createCaptivePortalUuid);
    if (!isCreateResponse) return;
    setIsLoading(false);

    if (checkMockup) {
      if (isCreateResponse && isCreateResponse?.data) {
        reloadPage(isCreateResponse.data);
        handleCloseModal();
      }
    } else {
      if (isCreateResponse) {
        if (isCreateResponse.hasOwnProperty('code') && isCreateResponse?.code !== 200) {
          setAlertInfo({
            open: true,
            severity: 'error',
            message: isCreateResponse?.message,
          });
        } else {
          initCaptivePortal();
          handleCloseModal();
        }
      }
    }
  }, [dataMap]);

  return (
    <>
      <ModalContainer display={openCreateModal}>
        {isLoading && <Loading />}
        <Box sx={{ padding: '0 0 20px 15px' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.customFontSize[5],
                fontWeight: theme.typography.customFontWeight[3],
              }}
            >
              Add Captive Portal
            </Typography>
            <Button
              onClick={handleCloseModal}
              sx={{
                backgroundColor: theme.palette.primary.transparent,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.primary.transparent,
                },
              }}
            >
              <CloseIcon />
            </Button>
          </Box>

          {/* General */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: '20px',
              gap: '8px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                transform: openGeneral ? 'rotate(90deg)' : 'rotate(0deg)',
                cursor: 'pointer',
              }}
              onClick={() => setOpenGeneral(!openGeneral)}
            >
              <ChevronSolidIcon />
            </Box>
            <Typography
              sx={{
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize[2],
                fontWeight: theme.typography.customFontWeight[1],
              }}
            >
              General
            </Typography>
          </Box>
          <Collapse in={openGeneral}>
            {/* Profile Name */}
            <Box sx={{ ...rowStyle, marginBottom: hasDuplicateName ? '10px' : '0px' }}>
              <Box sx={{ ...labelStyle, paddingTop: hasDuplicateName ? '0px' : '14px', height: hasDuplicateName ? '42px' : '50px' }}>Profile Name</Box>
              <Box>
                <TextField
                  placeholder="e.g. Pro WiFi Access"
                  sx={textfieldStyle}
                  autoComplete="off"
                  inputProps={{
                    minLength: 1,
                    maxLength: 64,
                  }}
                  value={formData?.profileName || ''}
                  onChange={e => {
                    setFormData(prev => ({ ...prev, profileName: e.target.value }));
                    setHasDuplicateName(tableData?.some(item => item.captivePortalName === e.target.value));
                  }}
                  error={hasDuplicateName || (!isNil(formData?.profileName) && size(formData?.profileName) === 0)}
                  helperText={hasDuplicateName ? 'This name already exists in this site.' : ''}
                />
              </Box>
            </Box>
            {/* Authentication Type */}
            <Box sx={rowStyle}>
              <Box sx={{ ...labelStyle, paddingTop: '5px', height: '30px' }}>Authentication Type</Box>
              <TypeSelectMenu
                width={textfieldStyle.width}
                value={formData?.authTypeList?.find(item => item.isActive)?.value || 'CLICK_THROUGH'}
                options={formData?.authTypeList}
                onChange={obj => handleChangeItem('authTypeList', obj.value)}
                isUnityPro={isUnityPro}
              />
            </Box>
            {/* Simultaneous Login */}
            {showSimultaneousLoginInfo && (
              <Box sx={rowStyle}>
                <Box sx={{ ...labelStyle }}>Simultaneous Login</Box>
                <Box>
                  <Box sx={{ display: 'flex' }}>
                    <Box sx={{ width: 100 }}>
                      <RadioButton
                        label="Enabled"
                        name="simultaneousLoginOption"
                        value={'ENABLE'}
                        checked={formData?.simultaneousLoginOption === 'ENABLE'}
                        onChange={() => setFormData(prev => ({ ...prev, simultaneousLoginOption: 'ENABLE' }))}
                      />
                    </Box>
                    <Box sx={{ width: 100 }}>
                      <RadioButton
                        label="Disabled"
                        name="simultaneousLoginOption"
                        value={'DISABLE'}
                        checked={formData?.simultaneousLoginOption === 'DISABLE'}
                        onChange={() => setFormData(prev => ({ ...prev, simultaneousLoginOption: 'DISABLE' }))}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
            {/* Idle Timeout */}
            {showIdleTimeoutInfo && (
              <Box sx={rowStyle}>
                <Box sx={{ ...labelStyle, height: '50px' }}>Idle Timeout</Box>
                <Box>
                  <Box sx={{ display: 'flex' }}>
                    <Box sx={{ width: 100 }}>
                      <RadioButton
                        label="Default"
                        name="idleTimeoutOption"
                        value={'DEFAULT'}
                        checked={formData?.idleTimeoutOption === 'DEFAULT'}
                        onChange={() => setFormData(prev => ({ ...prev, idleTimeoutOption: 'DEFAULT' }))}
                      />
                    </Box>
                    <Box style={{ width: 100 }}>
                      <RadioButton
                        label="Custom"
                        name="idleTimeoutOption"
                        value={'CUSTOM'}
                        checked={formData?.idleTimeoutOption === 'CUSTOM'}
                        onChange={() => setFormData(prev => ({ ...prev, idleTimeoutOption: 'CUSTOM' }))}
                      />
                    </Box>
                  </Box>
                  {formData?.idleTimeoutOption === 'DEFAULT' && (
                    <MenuSelect
                      width={textfieldStyle.width}
                      value={formData?.idleTimeoutList?.find(item => item.isActive)?.value}
                      options={formData?.idleTimeoutList}
                      onChange={obj => handleChangeItem('idleTimeoutList', obj.value)}
                    />
                  )}
                  {formData?.idleTimeoutOption === 'CUSTOM' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 90,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#aaa',
                          pointerEvents: 'none',
                        }}
                      >
                        Min(s)
                      </Box>

                      <TextField
                        sx={textfieldStyle}
                        autoComplete="off"
                        type="number"
                        min="2"
                        max="1440"
                        value={formData?.idleTimeoutLabel}
                        onChange={e => setFormData(prev => ({ ...prev, idleTimeoutLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                        error={formData?.idleTimeoutLabel < 2 || formData?.idleTimeoutLabel > 1440}
                      />
                      <Typography sx={{ marginLeft: '10px', color: theme.palette.primary.greySecound }}>(2~1440)</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            {/* Authentication Timeout */}
            {showAuthTimeoutInfo && (
              <Box sx={rowStyle}>
                <Box sx={{ ...labelStyle, height: formData?.authTimeoutOption === 'DISABLE' ? '20px' : '50px' }}>Authentication Timeout</Box>
                <Box>
                  <Box sx={{ display: 'flex' }}>
                    <Box sx={{ width: 100 }}>
                      <RadioButton
                        label="Disabled"
                        name="authTimeoutOption"
                        value={'DISABLE'}
                        checked={formData?.authTimeoutOption === 'DISABLE'}
                        onChange={() => setFormData(prev => ({ ...prev, authTimeoutOption: 'DISABLE' }))}
                      />
                    </Box>
                    <Box sx={{ width: 100 }}>
                      <RadioButton
                        label="Enabled"
                        name="authTimeoutOption"
                        value={'ENABLE'}
                        checked={formData?.authTimeoutOption === 'ENABLE'}
                        onChange={() => setFormData(prev => ({ ...prev, authTimeoutOption: 'ENABLE' }))}
                      />
                    </Box>
                    <Box sx={{ width: 100 }}>
                      <RadioButton
                        label="Custom"
                        name="authTimeoutOption"
                        value={'CUSTOM'}
                        checked={formData?.authTimeoutOption === 'CUSTOM'}
                        onChange={() => setFormData(prev => ({ ...prev, authTimeoutOption: 'CUSTOM' }))}
                      />
                    </Box>
                  </Box>

                  {formData?.authTimeoutOption === 'ENABLE' && (
                    <MenuSelect
                      width={textfieldStyle.width}
                      value={formData?.authTimeoutList?.find(item => item.isActive)?.value}
                      options={formData?.authTimeoutList}
                      onChange={obj => handleChangeItem('authTimeoutList', obj.value)}
                    />
                  )}
                  {formData?.authTimeoutOption === 'CUSTOM' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 90,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#aaa',
                          pointerEvents: 'none',
                        }}
                      >
                        Min(s)
                      </Box>
                      <TextField
                        sx={textfieldStyle}
                        autoComplete="off"
                        type="number"
                        min="2"
                        max="1440"
                        value={formData?.authTimeoutLabel}
                        onChange={e => setFormData(prev => ({ ...prev, authTimeoutLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                        error={formData?.authTimeoutLabel < 2 || formData?.authTimeoutLabel > 1440}
                      />
                      <Typography sx={{ marginLeft: '10px', color: theme.palette.primary.greySecound }}>(2~1440)</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            {/* Authentication Per Day */}
            {showAuthPerDayInfo && (
              <Box sx={rowStyle}>
                <Box sx={{ ...labelStyle, height: formData?.authPerDayOption === 'CUSTOM' ? '50px' : '20px' }}>Authentication Per Day</Box>
                <Box>
                  <Box sx={{ display: 'flex' }}>
                    <Box sx={{ width: 100 }}>
                      <RadioButton
                        label="Custom"
                        name="authPerDayOption"
                        value={'CUSTOM'}
                        checked={formData?.authPerDayOption === 'CUSTOM'}
                        onChange={() => setFormData(prev => ({ ...prev, authPerDayOption: 'CUSTOM' }))}
                      />
                    </Box>
                    <Box sx={{ width: 100 }}>
                      <RadioButton
                        label="Unlimited"
                        name="authPerDayOption"
                        value={'UNLIMIT'}
                        checked={formData?.authPerDayOption === 'UNLIMIT'}
                        onChange={() => setFormData(prev => ({ ...prev, authPerDayOption: 'UNLIMIT' }))}
                      />
                    </Box>
                  </Box>

                  {formData?.authPerDayOption === 'CUSTOM' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 80,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#aaa',
                          pointerEvents: 'none',
                        }}
                      >
                        Time/Day
                      </Box>
                      <TextField
                        sx={textfieldStyle}
                        autoComplete="off"
                        type="number"
                        min="1"
                        max="720"
                        value={formData?.authPerDayLabel}
                        onChange={e => setFormData(prev => ({ ...prev, authPerDayLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                        error={formData?.authPerDayLabel < 1 || formData?.authPerDayLabel > 720}
                      />
                      <Typography sx={{ marginLeft: '10px', color: theme.palette.primary.greySecound }}>(1~720)</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            {/* Re-Authentication Interval */}
            {showReAuthIntervalInfo && (
              <Box sx={rowStyle}>
                <Box sx={{ ...labelStyle, height: formData?.reAuthIntervalOption === 'DISABLE' ? '20px' : '50px' }}>Re-Authentication Interval</Box>
                <Box>
                  <Box sx={{ display: 'flex' }}>
                    <Box sx={{ width: 100 }}>
                      <RadioButton
                        label="Disabled"
                        name="reAuthIntervalOption"
                        value={'DISABLE'}
                        checked={formData?.reAuthIntervalOption === 'DISABLE'}
                        onChange={() => setFormData(prev => ({ ...prev, reAuthIntervalOption: 'DISABLE' }))}
                      />
                    </Box>
                    <Box sx={{ width: 100 }}>
                      <RadioButton
                        label="Enabled"
                        name="reAuthIntervalOption"
                        value={'ENABLE'}
                        checked={formData?.reAuthIntervalOption === 'ENABLE'}
                        onChange={() => setFormData(prev => ({ ...prev, reAuthIntervalOption: 'ENABLE' }))}
                      />
                    </Box>
                    <Box sx={{ width: 100 }}>
                      <RadioButton
                        label="Custom"
                        name="reAuthIntervalOption"
                        value={'CUSTOM'}
                        checked={formData?.reAuthIntervalOption === 'CUSTOM'}
                        onChange={() => setFormData(prev => ({ ...prev, reAuthIntervalOption: 'CUSTOM' }))}
                      />
                    </Box>
                  </Box>

                  {formData?.reAuthIntervalOption === 'ENABLE' && (
                    <MenuSelect
                      width={textfieldStyle.width}
                      value={formData?.reAuthIntervalList?.find(item => item.isActive)?.value}
                      options={formData?.reAuthIntervalList}
                      onChange={obj => handleChangeItem('reAuthIntervalList', obj.value)}
                    />
                  )}
                  {formData?.reAuthIntervalOption === 'CUSTOM' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 80,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#aaa',
                          pointerEvents: 'none',
                        }}
                      >
                        Min(s)
                      </Box>
                      <TextField
                        sx={textfieldStyle}
                        autoComplete="off"
                        type="number"
                        min="1"
                        max="720"
                        value={formData?.reAuthIntervalLabel}
                        onChange={e => setFormData(prev => ({ ...prev, reAuthIntervalLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                        error={formData?.reAuthIntervalLabel < 1 || formData?.reAuthIntervalLabel > 720}
                      />
                      <Typography sx={{ marginLeft: '10px', color: theme.palette.primary.greySecound }}>(1~720)</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            {/* User Profile */}
            {showUserProfileInfo && (
              <Box sx={rowStyle}>
                <Box sx={{ ...labelStyle }}>User Profile</Box>
                <MenuSelect
                  width={textfieldStyle.width}
                  value={formData?.userProfileList?.find(item => item.isActive)?.value}
                  options={formData?.userProfileList}
                  onChange={obj => handleChangeItem('userProfileList', obj.value)}
                />
              </Box>
            )}
            {/* RADIUS Profile */}
            {showRadiusProfileInfo && (
              <Box sx={rowStyle}>
                <Box sx={{ ...labelStyle }}>RADIUS Profile</Box>
                <MenuSelect
                  width={textfieldStyle.width}
                  value={formData?.radiusProfileList?.find(item => item.isActive)?.value}
                  options={formData?.radiusProfileList}
                  onChange={obj => handleChangeItem('radiusProfileList', obj.value)}
                />
              </Box>
            )}
            {/* LDAP Profile */}
            {showLdapProfileInfo && (
              <Box sx={rowStyle}>
                <Box sx={{ ...labelStyle }}>LDAP Profile</Box>
                <MenuSelect
                  width={textfieldStyle.width}
                  value={formData?.ldapProfileList?.find(item => item.isActive)?.value}
                  options={formData?.ldapProfileList}
                  onChange={obj => handleChangeItem('ldapProfileList', obj.value)}
                />
              </Box>
            )}
            {/* External Captive Portal */}
            {showExternalCaptivePortalInfo && (
              <Box sx={rowStyle}>
                <Box sx={{ ...labelStyle }}>External Captive Portal</Box>
                <UrlInput
                  maxLength={formData?.externalCaptivePortalProtocol === 'https' ? 2075 : 2076} // Custom landing page URL max 2083 characters.
                  protocol={formData?.externalCaptivePortalProtocol}
                  onChangeProtocol={protocol => setFormData(prev => ({ ...prev, externalCaptivePortalProtocol: protocol }))}
                  inputValue={formData?.externalCaptivePortalLabel || ''}
                  onChangeInputValue={newUrlInputValue => setFormData(prev => ({ ...prev, externalCaptivePortalLabel: newUrlInputValue }))}
                  error={
                    (!isNil(formData?.externalCaptivePortalLabel) && size(formData?.externalCaptivePortalLabel) === 0) ||
                    (size(formData?.externalCaptivePortalLabel) > 0 && !validUrl(formData?.externalCaptivePortalLabel))
                  }
                />
              </Box>
            )}
          </Collapse>

          {/* Advanced Settings */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: '20px',

              gap: '8px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                transform: openAdvancedSettings ? 'rotate(90deg)' : 'rotate(0deg)',
                cursor: 'pointer',
              }}
              onClick={() => setOpenAdvancedSettings(!openAdvancedSettings)}
            >
              <ChevronSolidIcon />
            </Box>
            <Typography
              sx={{
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize[2],
                fontWeight: theme.typography.customFontWeight[1],
              }}
            >
              Advanced Settings
            </Typography>
          </Box>
          <Collapse in={openAdvancedSettings}>
            {/* Landing Page */}
            {showLandingPageInfo && (
              <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
                <Box sx={{ ...labelStyle, paddingTop: '10px', display: 'flex' }}>
                  <Typography sx={{ fontSize: theme.typography.customFontSize[2], marginRight: 1 }}>Landing Page</Typography>
                  <TooltipNotice placement="bottom" tooltipTitle={<Box sx={{ width: 250 }}>Clients will be directed to the page after they pass Portal authentication.</Box>} />
                </Box>
                <Box sx={{ display: 'grid' }}>
                  <RadioButton
                    label="Not Specified"
                    name="landingPageOption"
                    value={'NO_SPECIFIC'}
                    checked={formData?.landingPageOption === 'NO_SPECIFIC'}
                    onChange={() => setFormData(prev => ({ ...prev, landingPageOption: 'NO_SPECIFIC' }))}
                  />
                  <RadioButton
                    label="The Promotional URL"
                    name="landingPageOption"
                    value={'CUSTOM'}
                    checked={formData?.landingPageOption === 'CUSTOM'}
                    onChange={() => setFormData(prev => ({ ...prev, landingPageOption: 'CUSTOM' }))}
                  />
                  {formData?.landingPageOption === 'CUSTOM' && (
                    <Box sx={{ width: '200px', marginBottom: '10px' }}>
                      <UrlInput
                        maxLength={formData?.landingPageUrlProtocol === 'https' ? 2075 : 2076} // Custom landing page URL max 2083 characters.
                        placeholder="www.dlink.com"
                        protocol={formData?.landingPageUrlProtocol}
                        onChangeProtocol={protocol => setFormData(prev => ({ ...prev, landingPageUrlProtocol: protocol }))}
                        inputValue={formData?.landingPageUrlLabel || ''}
                        onChangeInputValue={newUrlInputValue => setFormData(prev => ({ ...prev, landingPageUrlLabel: newUrlInputValue }))}
                        error={
                          (!isNil(formData?.landingPageUrlLabel) && size(formData?.landingPageUrlLabel) === 0) ||
                          (size(formData?.landingPageUrlLabel) > 0 && !validUrl(formData?.landingPageUrlLabel))
                        }
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            {/* Whitelist */}
            {showWhitelistInfo && (
              <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
                <Box sx={{ ...labelStyle, paddingTop: '4px', display: 'flex' }}>
                  <Typography sx={{ fontSize: theme.typography.customFontSize[2], marginRight: 1 }}>Whitelist</Typography>
                  <TooltipNotice
                    placement="bottom"
                    tooltipTitle={
                      <Box sx={{ width: 270 }}>To set up the Whitelist, add one or more existing MAC Address Profiles. A maximum of 64 profiles can be configured.</Box>
                    }
                  />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <SelectMenu menuList={formData?.whitelistList} onChangeItem={newValue => handleChangeItem('whitelistList', newValue)} />

                  {macLimitExceeded && (
                    <Box sx={{ marginTop: '8px', paddingLeft: '14px' }}>
                      <Typography sx={{ fontSize: '12px', color: theme.palette.primary.redPrimary }}>
                        The selected MAC Address Profile has exceeded the maximum limit of 64 MAC addresses.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            {/* Pre-Auth. Access (Walled Garden) */}
            {showPreAuthAccessInfo && (
              <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
                <Box sx={{ ...labelStyle, marginTop: '10px' }}>Pre-Auth. Access (Walled Garden)</Box>
                <Box sx={{ width: '580px' }}>
                  <Box sx={{ display: 'flex' }}>
                    <Box sx={{ width: 100 }}>
                      <RadioButton
                        label="Enabled"
                        name="preAuthAccessOption"
                        value={'ENABLE'}
                        checked={formData?.preAuthAccessOption === 'ENABLE'}
                        onChange={() => setFormData(prev => ({ ...prev, preAuthAccessOption: 'ENABLE' }))}
                      />
                    </Box>
                    <Box sx={{ width: 100 }}>
                      <RadioButton
                        label="Disabled"
                        name="preAuthAccessOption"
                        value={'DISABLE'}
                        checked={formData?.preAuthAccessOption === 'DISABLE'}
                        onChange={() => setFormData(prev => ({ ...prev, preAuthAccessOption: 'DISABLE' }))}
                      />
                    </Box>
                  </Box>
                  {formData?.preAuthAccessOption === 'ENABLE' && (
                    <Box>
                      {/* Walled Garden List */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', marginBottom: '5px' }}>
                        <Box>
                          <Typography
                            sx={{
                              color: theme.palette.primary.greyPrimary,
                              fontSize: theme.typography.customFontSize[2],
                              fontWeight: theme.typography.customFontWeight[1],
                            }}
                          >
                            Walled Garden List
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex' }}>
                          <Typography sx={{ fontSize: theme.typography.customFontSize[2], fontWeight: 'bold', paddingRight: '5px' }}>
                            {formData?.walledGardenList?.length}
                          </Typography>
                          <Typography sx={{ fontSize: theme.typography.customFontSize[2] }}>/ 20 Entries Used</Typography>
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: theme.typography.customFontSize[2] }}>Please enter a valid IP address, IP address/subnet, or domain name.</Typography>
                      <Box
                        className={mainStyle['custom-scrollbar']}
                        sx={{
                          marginTop: '10px',
                          border: `1px solid ${theme.palette.primary.greyThread}`,
                          borderRadius: '10px',
                          maxHeight: '242px',
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          boxSizing: 'content-box',
                        }}
                      >
                        {formData?.walledGardenList?.map((item, index) => (
                          <Box
                            key={`${index}-${item?.value || item?.label || 'empty'}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: index % 2 === 0 ? theme.palette.primary.transparent : theme.palette.primary.whiteSecound,
                              borderBottomLeftRadius: formData?.walledGardenList?.length - 1 === index ? '10px' : '0px',
                              borderBottomRightRadius: formData?.walledGardenList?.length - 1 === index ? '10px' : '0px',
                            }}
                          >
                            <TextField
                              sx={{
                                ...textfieldStyle,
                                width: '100%',
                                minWidth: '300px',
                                padding: '15px 0px 15px 15px',
                              }}
                              autoComplete="off"
                              inputProps={{
                                minLength: 1,
                                maxLength: 64,
                              }}
                              value={walledGardenInputs[index] || ''}
                              onChange={e =>
                                setWalledGardenInputs(prev => {
                                  const next = [...prev];
                                  next[index] = e.target.value;
                                  return next;
                                })
                              }
                              onBlur={() => handleChangeWalledGardenItem(index, walledGardenInputs[index] || '')}
                              error={(Boolean(walledGardenInputs[index]) && !isValidWalledGardenValue(walledGardenInputs[index])) || isWalledGardenValueDuplicate(index)}
                            />
                            <Box sx={{ width: '50px', paddingLeft: '10px' }}>
                              {index !== 0 && (
                                <IconButton
                                  sx={{ color: theme.palette.primary.main }}
                                  aria-label={`remove-walled-garden-${index}`}
                                  onClick={() => handleRemoveWalledGardenItem(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                      {/* Add button */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                        <OutlineRectangleButton
                          startIcon={!(formData?.walledGardenList?.length || 0) >= MAX_WALLED_GARDEN_ENTRIES ? <AddIcon /> : <AddIconDisabled />}
                          width="80px"
                          height="30px"
                          onClick={handleAddWalledGardenItem}
                          disabled={(formData?.walledGardenList?.length || 0) >= MAX_WALLED_GARDEN_ENTRIES}
                        >
                          Add
                        </OutlineRectangleButton>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Collapse>
        </Box>
        <Box
          sx={{
            paddingTop: '20px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <OutlineButton sx={{ marginRight: '20px' }} width="100px" height="32px" onClick={handleCloseModal}>
            Cancel
          </OutlineButton>
          <SolidButton width="100px" height="32px" onClick={checkSaveProRule} disabled={checkConfigInvalid()}>
            Create
          </SolidButton>
        </Box>
      </ModalContainer>

      <LinkModal
        transparentBackdrop
        open={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        onLinkClick={() => {
          setIsLinkModalOpen(false);
          const isMspScope = auth?.realm?.hasManageOrg === true && !auth?.orgs?.id;
          navigate(isMspScope ? '/msp/license' : '/license');
        }}
      />

      <GlobalAlert open={alertInfo.open} severity={alertInfo.severity} message={alertInfo.message} onClose={() => setAlertInfo(prev => ({ ...prev, open: false }))} />
    </>
  );
};

export default AddCaptivePortalModal;
